import { DialogTitle } from "../../Dialog";
import {
	CSVFileUploads,
	HeaderTitleStyles,
	NewOrderModalDisclaimer,
	UnsetSecondaryCopy,
	UploadCSVDragDropButton,
	UploadCSVDragDropContent,
	UploadCSVDragDropUploadFile,
	UploadModalCSVFileInfoContainerButton
} from "../NewOrders.styles";
import {
	NewOrderContinueButton,
	NewOrderModalDisclaimerText,
	NewOrderModalDragFile,
	NewOrderModalImportCSV,
	NewOrderModalSupportedFiles,
	NewOrderSelectedFiles
} from "../constants";
import { Content } from "pages/Replacements/components/AddNewAddressModal/addNewAddressModal.styles";
import UploadFile from "@mui/icons-material/UploadFile";
import { Button, Typography } from "@mui/material";
import { BrowseFiles, DownloadLatestText, HereText, SupportedFilesHeader } from "constants/text";
import Dragdrop from "../../Dragdrop/Dragdrop";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { useParseCatalogsMutation, useParseOrderMutation } from "features/api/newOrderApi.ts";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetAccountsQuery, useLazyGetAccountsByIdQuery } from "features/api/accountApi.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import Link from "@mui/material/Link";
import { salesforceBaseURL } from "constants/endpoints.ts";
import { RootState } from "stores/application.store.tsx";
import CSVFileUploadProgress from "../CSVFileUploadProgress/CSVFileUploadProgress";
import { trackGA4Event } from "utils/googleAnalytics";
import { models } from "types/api/viewModels";
import { convertToTitleCase } from "utils/string";
import GlobalFormProvider from "../AddGlobalAttributes/forms/GlobalFormProvider";
import AddGlobalAttributes from "../AddGlobalAttributes/AddGlobalAttributes";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { useDialog } from "hooks/useDialog.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { doDispatches, fetchProductLineAccounts } from "../utils/NewOrderUtils.tsx";

// Symbol for fileName metadata
export const fileNameSymbol = Symbol("fileName");

interface CsvUploadProps {
	handleClose: () => void;
}

export interface ErrorCodeData {
	invalidCSVData?: boolean;
	invalidCSVFile?: boolean;
	invalidCSVType?: boolean;
	unauthorizedProductLine?: boolean;
}

interface CSVFile extends File, ErrorCodeData {
	isSuccess?: boolean;
	errors?: any;
	isAttemptedValidation: boolean;
}

const CSVUpload = ({ handleClose }: CsvUploadProps) => {
	const [csvFile, setCSVFile] = useState<CSVFile[]>([]);
	const [parseCatalogs] = useParseCatalogsMutation();
	const [parseOrder] = useParseOrderMutation();
	const [showFixGlobalsModal, setShowFixGlobalsModal] = useState(false);
	const [invalidCatalogs, setInvalidCatalogs] = useState<models["CatalogViewModel"][]>([]);
	const [allValidCatalogsByFile, setAllValidCatalogsByFile] = useState<Record<string, models["CatalogViewModel"][]>>(
		{}
	);
	const [getAccountByProductLine] = useLazyGetAccountsQuery();
	const [getAccountsByOrderId] = useLazyGetAccountsByIdQuery();
	const { orderId } = useParams<{ orderId: string }>();
	const dispatch = useDispatch();
	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const [hasInvalidCatalogs, setHasInvalidCatalogs] = useState(false);
	const [hasCustomFinishCatalogs, setHasCustomFinishCatalogs] = useState(false);
	const [showLoadingAfterFix, setShowLoadingAfterFix] = useState(false);
	const { isAddOnPage, isNewOrderBuildOrderPage, isAddOnBuildOrderPage } = useUserLocation();
	const accountFetcher =
		isAddOnPage && orderId
			? (_productLineCode?: string | null) => getAccountsByOrderId(orderId)
			: getAccountByProductLine;
	const { openDialog } = useDialog();
	const { configurationsData } = useContext(ConfigurationsContext);
	const catalogs = useSelector((state: RootState) => state.newOrder.catalogs);

	const updateFiles = (selectedFiles: Array<CSVFile>) => {
		const updatedFiles = selectedFiles.map((file) =>
			Object.assign(new File([file], file.name, { type: file.type, lastModified: file.lastModified }), {
				isAttemptedValidation: false
			})
		);
		setCSVFile((prev) => [...prev, ...updatedFiles]);
	};

	const onClose = () => {
		handleClose();
	};

	const [isUpdating, setIsUpdating] = useState<boolean>(false);

	const orderType = isAddOnPage ? "add ons" : "new order";

	const markFileAsAttempted = (fileNumber: number) => {
		setCSVFile((prev) =>
			prev.map((file, index) =>
				index === fileNumber
					? Object.assign(new File([file], file.name, { type: file.type, lastModified: file.lastModified }), {
							isAttemptedValidation: true
						})
					: file
			)
		);
	};

	const processFile = async (
		file: CSVFile,
		fileNumber: number,
		allValid: Record<string, models["CatalogViewModel"][]>,
		allInvalid: models["CatalogViewModel"][],
		allCustomFinishCatalogs: models["CatalogViewModel"][],
		allNonCustomFinishCatalogs: models["CatalogViewModel"][]
	) => {
		markFileAsAttempted(fileNumber);

		const { name, lastModified, type } = file;
		const fileWithoutErrorText = new File([file], name, { type, lastModified });
		const formData = new FormData();
		formData.append("file", fileWithoutErrorText);

		try {
			const parsedCatalogs = await parseCatalogs(formData).unwrap();
			const valid = (parsedCatalogs || []).filter((cat: models["CatalogViewModel"]) => cat.isValid);
			const invalid = (parsedCatalogs || []).filter((cat: models["CatalogViewModel"]) => !cat.isValid);
			const customFinishCatalogs = (parsedCatalogs || []).filter(
				(cat: models["CatalogViewModel"]) => cat.globals?.finish?.isCustomFinish
			);
			const nonCustomFinishCatalogs = (parsedCatalogs || []).filter(
				(cat: models["CatalogViewModel"]) => !cat.globals?.finish?.isCustomFinish
			);
			allInvalid.push(
				...invalid.map((cat: models["CatalogViewModel"]) => ({
					...cat,
					[fileNameSymbol]: file.name
				}))
			);
			allCustomFinishCatalogs.push(
				...customFinishCatalogs.map((cat: models["CatalogViewModel"]) => ({
					...cat,
					[fileNameSymbol]: file.name
				}))
			);
			allNonCustomFinishCatalogs.push(
				...nonCustomFinishCatalogs.map((cat: models["CatalogViewModel"]) => ({
					...cat,
					[fileNameSymbol]: file.name
				}))
			);
			if (valid.length > 0) {
				allValid[file.name] = valid.map((cat: models["CatalogViewModel"]) => ({
					...cat,
					[fileNameSymbol]: file.name
				}));
			}

			setCSVFile((prev) =>
				prev.map((f, idx) =>
					idx === fileNumber
						? Object.assign(new File([f], f.name, { type: f.type, lastModified: f.lastModified }), {
								isAttemptedValidation: true,
								isSuccess: true
							})
						: f
				)
			);
			return false;
		} catch (error: any) {
			handleCSVError(fileNumber, error, orderType);
			return true;
		}
	};

	const validateAllFiles = async () => {
		setIsUpdating(true);
		let allInvalid: models["CatalogViewModel"][] = [];
		let allCustomFinishCatalogs: models["CatalogViewModel"][] = [];
		let allNonCustomFinishCatalogs: models["CatalogViewModel"][] = [];
		let allValid: Record<string, models["CatalogViewModel"][]> = {};
		const filesWithInvalidCatalogs = new Set<string>();
		const filesWithCustomFinishCatalogs = new Set<string>();
		const filesWithoutCustomFinishCatalogs = new Set<string>();

		for (let fileNumber = 0; fileNumber < csvFile.length; fileNumber++) {
			const file = csvFile[fileNumber];
			if (!file || file.isAttemptedValidation) continue;
			await processFile(
				file,
				fileNumber,
				allValid,
				allInvalid,
				allCustomFinishCatalogs,
				allNonCustomFinishCatalogs
			);
		}

		allCustomFinishCatalogs.forEach((cat: models["CatalogViewModel"]) => {
			const fileName = cat[fileNameSymbol];
			if (fileName) filesWithCustomFinishCatalogs.add(fileName);
		});
		allNonCustomFinishCatalogs.forEach((cat: models["CatalogViewModel"]) => {
			const fileName = cat[fileNameSymbol];
			if (fileName) filesWithoutCustomFinishCatalogs.add(fileName);
		});
		allInvalid.forEach((cat) => {
			const fileName = cat[fileNameSymbol];
			if (fileName) filesWithInvalidCatalogs.add(fileName);
		});

		dispatch(
			newOrderActions.addCustomFinishCatalogs({
				catalogsWithCustomFinish: allCustomFinishCatalogs,
				catalogsWithoutCustomFinish: allNonCustomFinishCatalogs
			})
		);

		setInvalidCatalogs(allInvalid);
		setAllValidCatalogsByFile(allValid);

		let parsedCSVs: models["ValidatedOrderViewModel"][] = [];
		for (const [fileName, validCatalogsForFile] of Object.entries(allValid)) {
			if (
				validCatalogsForFile.length > 0 &&
				!filesWithInvalidCatalogs.has(fileName) &&
				!filesWithCustomFinishCatalogs.has(fileName)
			) {
				const importedCSVData = await parseOrder(validCatalogsForFile).unwrap();
				parsedCSVs.push({ ...importedCSVData, fileName });
			}
		}
		if (parsedCSVs.length > 0) {
			for (const csv of parsedCSVs) {
				doDispatches(csv, dispatch, isNewOrderBuildOrderPage, isAddOnBuildOrderPage, draftOrder);
				await fetchProductLineAccounts(csv, dispatch, accountFetcher);
			}
		}

		if (allInvalid.length > 0) {
			setInvalidCatalogs(allInvalid);
			setHasInvalidCatalogs(true);
			setIsUpdating(false);
			return;
		}

		if (allCustomFinishCatalogs.length > 0 && configurationsData?.featureFlags?.customColorEnabled) {
			setHasCustomFinishCatalogs(true);
		}

		setIsUpdating(false);
	};

	const handleGlobalsFixed = async (fixedInvalidCatalogs: models["CatalogViewModel"][]) => {
		if (catalogs?.catalogsWithCustomFinish && catalogs?.catalogsWithCustomFinish.length > 0) {
			let newCatalogsWithCustomFinish = [
				...(catalogs?.catalogsWithCustomFinish ?? []),
				...(catalogs?.catalogsWithoutCustomFinish ?? [])
			];
			newCatalogsWithCustomFinish = newCatalogsWithCustomFinish.map((catalog) => {
				const foundCatalogFromFixedInvalidCatalogs = fixedInvalidCatalogs.find(
					(fixed) => fixed.name === catalog.name
				);
				return foundCatalogFromFixedInvalidCatalogs ?? catalog;
			});
			const onlyCatalogsWithCustomFinish = newCatalogsWithCustomFinish.filter(
				(catalog) => catalog.globals?.finish?.isCustomFinish
			);
			const onlyCatalogsWithoutCustomFinish = newCatalogsWithCustomFinish.filter(
				(catalog) => !catalog.globals?.finish?.isCustomFinish
			);
			dispatch(
				newOrderActions.addCustomFinishCatalogs({
					catalogsWithoutCustomFinish: onlyCatalogsWithoutCustomFinish,
					catalogsWithCustomFinish: onlyCatalogsWithCustomFinish
				})
			);
			openDialog({ dialogStatus: DialogTracking.SELECT_CUSTOM_COLOR });
		} else {
			setShowLoadingAfterFix(true);

			const fixedByFile: Record<string, models["CatalogViewModel"][]> = {};
			for (const cat of fixedInvalidCatalogs) {
				const fileName = cat[fileNameSymbol];
				if (!fixedByFile[fileName!]) {
					fixedByFile[fileName!] = [];
				}
				fixedByFile[fileName!]!.push(cat);
			}

			let parsedCSVs: models["ValidatedOrderViewModel"][] = [];
			const fixedFileNames = Object.keys(fixedByFile);

			for (const fileName of fixedFileNames) {
				const prevValidForFile = allValidCatalogsByFile[fileName] || [];
				const merged = [...prevValidForFile, ...(fixedByFile[fileName] ?? [])];

				const importedCSVData = await parseOrder(merged).unwrap();
				parsedCSVs.push({ ...importedCSVData, fileName });
			}

			setInvalidCatalogs([]);

			if (parsedCSVs.length > 0) {
				for (const csv of parsedCSVs) {
					doDispatches(csv, dispatch, isNewOrderBuildOrderPage, isAddOnBuildOrderPage, draftOrder);
					await fetchProductLineAccounts(csv, dispatch, accountFetcher);
				}
			}

			setShowLoadingAfterFix(false);
			setShowFixGlobalsModal(false);
			openDialog({ dialogStatus: DialogTracking.SELECT_BILLTO_ACCOUNT });
		}
	};

	const handleCSVError = (fileNumber: number, error: any, orderType: string) => {
		trackGA4Event({
			event: `${convertToTitleCase(orderType)} - Landing Page - Import fail`,
			eventCategory: orderType,
			eventAction: "error",
			eventLabel: `${orderType} import csv failure`
		});
		const errorStatus = error.status;
		const errors = error.errors || [];
		const containsErrorCode = (code: number | string) => errors.some((err: any) => err.code === code);
		const errorCodes = {
			invalidCSVData: errorStatus === 400 && containsErrorCode(40003), // can't find global, missing columns, etc... display errors in bullet points
			invalidCSVFile: errorStatus === 400 && containsErrorCode(40004), // file is null, no configs
			invalidCSVType: errorStatus === 400 && containsErrorCode(40005), // not a quote CSV
			unauthorizedProductLine: errorStatus === 403 && containsErrorCode(4036) // unauthorized access to product line
		};
		setCSVFile((prev) =>
			prev.map((file, index) =>
				index === fileNumber
					? Object.assign(new File([file], file.name, { type: file.type, lastModified: file.lastModified }), {
							...errorCodes,
							errors,
							isAttemptedValidation: true
						})
					: file
			)
		);
		console.error(error);
	};

	useEffect(() => {
		setCSVFile([]);
		dispatch(newOrderActions.clearTempParsedCSV());
		dispatch(newOrderActions.clearParsedCSV());
	}, [dispatch]);

	const removeFile = (index: number) => {
		setCSVFile((prev) => prev.filter((_, fileNumber) => fileNumber !== index));
	};

	const continueAction = async () => {
		if (csvFile.some((file) => !file.isAttemptedValidation)) {
			await validateAllFiles();
		} else if (hasInvalidCatalogs) {
			setShowFixGlobalsModal(true);
		} else if (hasCustomFinishCatalogs) {
			openDialog({ dialogStatus: DialogTracking.SELECT_CUSTOM_COLOR });
		} else {
			openDialog({ dialogStatus: DialogTracking.SELECT_BILLTO_ACCOUNT });
		}
	};

	return (
		<div>
			{showFixGlobalsModal ? (
				<GlobalFormProvider isFixingGlobalAttributes={true}>
					<AddGlobalAttributes
						invalidCatalogs={invalidCatalogs}
						onGlobalsFixed={handleGlobalsFixed}
						isCustomColor={false}
						isFixingGlobalAttributes={true}
						isLoading={showLoadingAfterFix}
					/>
				</GlobalFormProvider>
			) : (
				<>
					<HeaderTitleStyles>
						<DialogTitle
							data-testid="new-order-csv-upload-title"
							title={NewOrderModalImportCSV}
							handleClose={onClose}
						/>
					</HeaderTitleStyles>

					<Content>
						<NewOrderModalDisclaimer data-testid="new-order-csv-upload-disclaimer">
							{!isUpdating ? <p>{NewOrderModalDisclaimerText}</p> : ""}
						</NewOrderModalDisclaimer>
						<Dragdrop
							accept="csv"
							onFileUpload={updateFiles}
						>
							<UploadCSVDragDropUploadFile>
								<UploadFile />
							</UploadCSVDragDropUploadFile>

							<UploadCSVDragDropContent>
								<p data-testid="new-order-csv-upload-drag-text">{NewOrderModalDragFile}</p>

								<UploadCSVDragDropButton>
									<Button
										variant="outlined"
										data-testid="new-order-csv-upload-browse-button"
									>
										{BrowseFiles}
									</Button>
								</UploadCSVDragDropButton>

								<p data-testid="new-order-csv-upload-supported-file-text">
									{SupportedFilesHeader}{" "}
									<span style={{ fontWeight: 500 }}>{NewOrderModalSupportedFiles}</span>
								</p>
							</UploadCSVDragDropContent>
						</Dragdrop>
						{csvFile.length > 0 && (
							<div>
								<Typography
									variant="subtitle1"
									sx={{ textTransform: "uppercase", letterSpacing: "1px" }}
									data-testid="new-order-csv-upload-selected-file-text"
								>
									{NewOrderSelectedFiles}
								</Typography>
								<CSVFileUploads>
									{csvFile.map((file, index) => (
										<CSVFileUploadProgress
											file={file}
											key={file.name}
											hasError={!!file.errors}
											errors={file.errors}
											isSuccess={file.isSuccess}
											isUpdating={isUpdating}
											isAttemptedValidation={file.isAttemptedValidation}
											errorCodes={{
												invalidCSVData: file.invalidCSVData,
												invalidCSVFile: file.invalidCSVFile,
												invalidCSVType: file.invalidCSVType,
												unauthorizedProductLine: file.unauthorizedProductLine
											}}
											removeFile={() => removeFile(index)}
											isAddOn={isAddOnPage}
										/>
									))}
									{csvFile.find(
										(file) => file.invalidCSVData || file.invalidCSVFile || file.invalidCSVType
									) && (
										<UnsetSecondaryCopy style={{ marginTop: "8px" }}>
											{DownloadLatestText}{" "}
											<Link
												href={`${salesforceBaseURL}/s/design-software`}
												data-testid="newOrder-download-latest-catalogs"
											>
												{HereText}
											</Link>
										</UnsetSecondaryCopy>
									)}
								</CSVFileUploads>

								{(!csvFile.find((file) => Boolean(file.errors)) ||
									csvFile.some((file) => !file.isAttemptedValidation)) &&
									!isUpdating && (
										<UploadModalCSVFileInfoContainerButton>
											<Button
												data-id={`${orderType.replaceAll(" ", "-")}-import-csv-button`}
												data-testid="new-order-csv-upload-selected-file-continue-button"
												variant="contained"
												onClick={continueAction}
											>
												{NewOrderContinueButton}
											</Button>
										</UploadModalCSVFileInfoContainerButton>
									)}
							</div>
						)}
					</Content>
				</>
			)}
		</div>
	);
};

export default CSVUpload;
